import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { mangaList } from "@/mocks/manga";
import { useTheme } from "@/hooks/useTheme";

// 漫画页面示例图（模拟分镜页）
const samplePages = [
  "https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20panel%20layout%20with%20action%20scene%20of%20a%20hero%20jumping%2C%20speech%20bubbles%2C%20dynamic%20panels%2C%20classic%20manga%20style%20illustration%2C%20high%20detail%20lineart&width=800&height=1200&seq=reader-page-01&orientation=portrait",
  "https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20panel%20layout%20of%20a%20close%20up%20face%20with%20intense%20expression%2C%20speech%20bubbles%2C%20speed%20lines%20background%2C%20classic%20manga%20style%2C%20detailed%20lineart&width=800&height=1200&seq=reader-page-02&orientation=portrait",
  "https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20panel%20layout%20of%20a%20wide%20battle%20scene%20with%20two%20characters%20clashing%20weapons%2C%20motion%20lines%2C%20classic%20manga%20style%2C%20dramatic%20composition%2C%20detailed%20lineart&width=800&height=1200&seq=reader-page-03&orientation=portrait",
  "https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20panel%20layout%20of%20a%20calm%20dialogue%20scene%20between%20two%20characters%2C%20speech%20bubbles%2C%20city%20background%2C%20classic%20manga%20style%2C%20clean%20detailed%20lineart&width=800&height=1200&seq=reader-page-04&orientation=portrait",
  "https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20panel%20layout%20of%20a%20flashback%20memory%20scene%20with%20soft%20panels%2C%20emotional%20character%2C%20classic%20manga%20style%2C%20expressive%20lineart&width=800&height=1200&seq=reader-page-05&orientation=portrait",
  "https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20panel%20layout%20of%20a%20climax%20action%20scene%20with%20explosive%20energy%2C%20speed%20lines%2C%20dramatic%20panels%2C%20classic%20manga%20style%2C%20high%20impact%20lineart&width=800&height=1200&seq=reader-page-06&orientation=portrait",
];

type ReadingMode = "paged" | "vertical" | "horizontal";

export default function Reader() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const manga = mangaList.find((m) => m.id === id);
  const [chapterIndex, setChapterIndex] = useState(() => {
    const c = Number(searchParams.get("chapter") || "0");
    return Number.isFinite(c) ? c : 0;
  });
  const [pageIndex, setPageIndex] = useState(0);
  const [mode, setMode] = useState<ReadingMode>("paged");
  const [doublePage, setDoublePage] = useState(false);
  const [showSetting, setShowSetting] = useState(false);
  const [cached, setCached] = useState<Set<string>>(new Set());
  const [showTools, setShowTools] = useState(true);
  const [viewWidth, setViewWidth] = useState<number>(() => (typeof window !== "undefined" ? window.innerWidth : 1200));

  const pageCount = manga?.chapters[chapterIndex]?.pages ?? 0;
  const pages = useMemo(
    () => Array.from({ length: pageCount }, (_, i) => samplePages[i % samplePages.length]),
    [pageCount],
  );

  useEffect(() => {
    const onResize = () => setViewWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const jumpChapter = (idx: number) => {
    const safeIdx = Math.max(0, Math.min((manga?.chapters.length || 1) - 1, idx));
    setChapterIndex(safeIdx);
    setPageIndex(0);
    navigate(`/reader/${id}?chapter=${safeIdx}`, { replace: true });
  };

  const toggleCache = (pi: number) => {
    setCached((prev) => {
      const next = new Set(prev);
      if (next.has(pi)) next.delete(pi);
      else next.add(pi);
      return next;
    });
  };

  const isLandscapeMode = mode === "horizontal" || (mode === "paged" && doublePage && viewWidth >= 1024);

  const displayedPages = isLandscapeMode
    ? [pages[pageIndex], pages[pageIndex + 1]].filter(Boolean)
    : [pages[pageIndex]];

  const nextPage = () => {
    if (pageIndex < pageCount - 1) setPageIndex(pageIndex + 1);
    else if (chapterIndex < (manga?.chapters.length || 1) - 1) jumpChapter(chapterIndex + 1);
  };

  const prevPage = () => {
    if (pageIndex > 0) setPageIndex(pageIndex - 1);
    else if (chapterIndex > 0) {
      jumpChapter(chapterIndex - 1);
      setPageIndex((manga?.chapters[chapterIndex - 1]?.pages || 1) - 1);
    }
  };

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center gap-4">
        <p className="text-foreground-600">漫画不存在</p>
        <Link to="/category" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium">返回分类</Link>
      </div>
    );
  }

  const chapter = manga.chapters[chapterIndex];
  const progress = `${pageIndex + 1} / ${pageCount}`;

  return (
    <div className="min-h-screen bg-background-950 flex flex-col">
      {/* 顶部栏 */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background-950/90 backdrop-blur border-b border-background-800">
        <div className="container-site h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={`/manga/${manga.id}`} className="w-8 h-8 rounded-full bg-background-800 flex items-center justify-center text-background-50 hover:bg-background-700 transition-colors shrink-0" aria-label="返回详情">
              <i className="ri-arrow-left-line"></i>
            </Link>
            <div className="min-w-0">
              <p className="text-background-50 font-medium text-sm truncate">{manga.title}</p>
              <p className="text-background-400 text-xs truncate">{chapter.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:block text-xs text-background-400">{progress}</span>
            <button
              type="button"
              onClick={() => setShowSetting(!showSetting)}
              className="w-8 h-8 rounded-full bg-background-800 flex items-center justify-center text-background-50 hover:bg-background-700 transition-colors"
              aria-label="阅读设置"
            >
              <i className="ri-settings-3-line text-sm"></i>
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              className="w-8 h-8 rounded-full bg-background-800 flex items-center justify-center text-background-50 hover:bg-background-700 transition-colors"
              aria-label="切换夜间模式"
            >
              <i className={theme === "dark" ? "ri-sun-line text-sm" : "ri-moon-line text-sm"}></i>
            </button>
          </div>
        </div>

        {/* 阅读设置面板 */}
        {showSetting && (
          <div className="border-t border-background-800 bg-background-900">
            <div className="container-site py-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-background-400 mb-2">阅读模式</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "paged", label: "翻页模式" },
                    { key: "vertical", label: "竖屏滚动" },
                    { key: "horizontal", label: "横屏阅读" },
                  ].map((m) => (
                    <button
                      key={m.key}
                      onClick={() => setMode(m.key as ReadingMode)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${
                        mode === m.key ? "bg-primary-500 text-background-50" : "bg-background-800 text-background-200 hover:bg-background-700"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-background-400 mb-2">单双页显示</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setDoublePage(false)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${!doublePage ? "bg-primary-500 text-background-50" : "bg-background-800 text-background-200 hover:bg-background-700"}`}
                  >
                    单页
                  </button>
                  <button
                    onClick={() => setDoublePage(true)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${doublePage ? "bg-primary-500 text-background-50" : "bg-background-800 text-background-200 hover:bg-background-700"}`}
                  >
                    双页
                  </button>
                </div>
              </div>
              <div>
                <p className="text-background-400 mb-2">缓存下载</p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setCached(new Set(pages.map((_, i) => i)))}
                    className="px-3 py-1.5 rounded-full text-xs bg-background-800 text-background-200 hover:bg-background-700 transition-colors whitespace-nowrap"
                  >
                    缓存本话
                  </button>
                  {cached.size > 0 && (
                    <span className="text-xs text-secondary-500 flex items-center gap-1">
                      <i className="ri-checkbox-circle-fill"></i> 已缓存 {cached.size} 页
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* 阅读区 */}
      <main
        className={`flex-1 pt-14 ${mode === "vertical" ? "overflow-y-auto" : "overflow-hidden"} ${mode === "horizontal" ? "flex items-center justify-center" : ""}`}
        onClick={() => setShowTools(!showTools)}
      >
        {mode === "vertical" ? (
          <div className="max-w-[720px] mx-auto py-6">
            {pages.map((pg, i) => (
              <div key={i} className={`relative ${i > 0 ? "mt-2" : ""}`}>
                <img src={pg} alt={`${manga.title} ${chapter.title} 第${i + 1}页`} className="w-full" loading="lazy" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleCache(i); }}
                  className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur transition-colors ${
                    cached.has(i) ? "bg-primary-500 text-background-50" : "bg-background-950/60 text-background-50 hover:bg-background-950/80"
                  }`}
                  aria-label="缓存此页"
                >
                  <i className={cached.has(i) ? "ri-check-line text-sm" : "ri-download-2-line text-sm"}></i>
                </button>
              </div>
            ))}
            <div className="pt-8 flex flex-col items-center gap-3 pb-10">
              <button onClick={(e) => { e.stopPropagation(); nextPage(); }} className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap">
                下一话
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`relative w-full h-full flex items-center justify-center ${mode === "horizontal" ? "px-8" : "px-4"}`}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prevPage(); }}
              className={`absolute ${isLandscapeMode && doublePage ? "left-6" : "left-3 md:left-4"} top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-950/50 backdrop-blur flex items-center justify-center text-background-50 hover:bg-background-950/80 transition-colors z-10 ${mode === "horizontal" ? "opacity-0 group-hover:opacity-100" : ""}`}
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>

            <div className={`flex items-center gap-3 max-h-full ${mode === "horizontal" ? "flex-row overflow-x-auto" : "flex-col overflow-y-auto"} justify-center`}>
              {displayedPages.map((pg, i) => (
                <div key={i} className={`relative shrink-0 ${mode === "horizontal" ? "" : "max-w-[440px] w-full"}`}>
                  <img
                    src={pg}
                    alt={`${manga.title} ${chapter.title} 第${pageIndex + i + 1}页`}
                    className={`${mode === "horizontal" ? "h-full max-h-[78vh]" : "w-full"} object-contain rounded-md`}
                    loading="lazy"
                  />
                  {!isLandscapeMode && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleCache(pageIndex); }}
                      className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur transition-colors ${
                        cached.has(pageIndex) ? "bg-primary-500 text-background-50" : "bg-background-950/60 text-background-50 hover:bg-background-950/80"
                      }`}
                      aria-label="缓存此页"
                    >
                      <i className={cached.has(pageIndex) ? "ri-check-line text-sm" : "ri-download-2-line text-sm"}></i>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); nextPage(); }}
              className={`absolute ${isLandscapeMode && doublePage ? "right-6" : "right-3 md:right-4"} top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-950/50 backdrop-blur flex items-center justify-center text-background-50 hover:bg-background-950/80 transition-colors z-10`}
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        )}
      </main>

      {/* 底部工具栏 */}
      <footer
        className={`fixed bottom-0 left-0 right-0 z-40 bg-background-950/90 backdrop-blur border-t border-background-800 transition-transform duration-300 ${
          showTools ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="container-site py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => jumpChapter(chapterIndex - 1)}
              disabled={chapterIndex === 0}
              className="px-3 py-1.5 rounded-full text-xs bg-background-800 text-background-200 hover:bg-background-700 disabled:opacity-40 transition-colors whitespace-nowrap"
            >
              上一话
            </button>
            <button
              type="button"
              onClick={() => jumpChapter(chapterIndex + 1)}
              disabled={chapterIndex >= manga.chapters.length - 1}
              className="px-3 py-1.5 rounded-full text-xs bg-background-800 text-background-200 hover:bg-background-700 disabled:opacity-40 transition-colors whitespace-nowrap"
            >
              下一话
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-xs text-background-400">{progress}</span>
            <button
              type="button"
              onClick={() => setShowSetting(!showSetting)}
              className="w-8 h-8 rounded-full bg-background-800 flex items-center justify-center text-background-200 hover:bg-background-700 transition-colors"
              aria-label="阅读设置"
            >
              <i className="ri-settings-3-line text-sm"></i>
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              className="w-8 h-8 rounded-full bg-background-800 flex items-center justify-center text-background-200 hover:bg-background-700 transition-colors"
              aria-label="夜间模式"
            >
              <i className={theme === "dark" ? "ri-sun-line text-sm" : "ri-moon-line text-sm"}></i>
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}